import React, { useState, useEffect } from 'react';
import { useUpdateEffect } from '@utils/hooks';
import PropTypes from 'prop-types';
import Icon from '../base_svg-icon/component';
import Typeset from '../base_typeset/component';
import Text from '@text';
import { t } from '@utils/i18n';
import { getFeature } from '@utils';
import Restapi from '@utils/api';
import { useAuth } from '@utils/authentication/AuthProvider';
import Skeleton from '@components/ui_skeleton/component';

import './styles.scss';

const StockCheck = (props) => {
  const { showIcon, store, product } = props;
  if (!product.stockCheck) {
    return null;
  }

  const feature = getFeature('stockCheck');
  const api = new Restapi();
  const auth = useAuth();

  const [storeAssigned, setStoreAssigned] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferredStoreId, setPreferredStoreId] = useState(null);

  const { user } = store.getBasket();
  useEffect(() => {
    if (showIcon) {
      setPreferredStoreId(user?.preferredStoreId);
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!auth.user) {
      setLoading(false);
    }
  }, []);

  useUpdateEffect(() => {
    if (user) {
      getStore();
    } else {
      setLoading(false);
    }
  }, [preferredStoreId]);

  const assignStore = (entry) => {
    if (!!entry?.availableQuantity) {
      api.getSingleStore(preferredStoreId)
        .then((response) => {
          const { stockCheck, stockCheckQuantore } = response;
          if ((product.stockCheck === 'stockCheck' && !!stockCheck) || (product.stockCheck === 'stockCheckQuantore' && !!stockCheckQuantore)) {
            setStoreAssigned(response);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const checkStock =  (valkStoreId) => {
    api.getStockInfo(product.SKU)
      .then((response) => {
        const matchingEntry = response.stockInfo.find(entry => entry.store.branchNumber === parseInt(valkStoreId, 10));
        setStockData({ storeId: matchingEntry?.store.id, productId: response.id });
        assignStore(matchingEntry);
      })
      .catch(() => setLoading(false));
  };

  const getStore = () => {
    if (preferredStoreId) {
      api.getSingleStore(preferredStoreId).then((response) => {
        checkStock(response.valkStoreID);
      });
    } else {
      setLoading(false);
    }
  };

  // Import the store component on click, so it's not inlcuded for every ankeiler
  const openStockCheck = () => {
    import('./_store-checker/component')
      .then((component) => {
        const StoreChecker = component.default;
        store.modal.openModal('popup', '', {
          title: t('stock.title'),
          component: <StoreChecker product={product} store={store} stockData={stockData} />,
          classNames: 'stock-check',
        });
      });
  };

  const openStockCollect = () => {
    import('./_store-checker/component')
      .then((component) => {
        const StoreChecker = component.default;
        store.modal.openModal('popup', '', {
          title: t('stock.title'),
          component: <StoreChecker product={product} store={store} storeAssigned={storeAssigned} stockData={stockData} />,
        });
      });
  };

  const showStockCheck = product.stockCheck !== null;
  if (!showStockCheck || !feature.isActive) {
    return null;
  }

  const incentive = storeAssigned ? {
    text: t('stock.available.incentive-text', { replacements: { shopname: storeAssigned.name } }),
    label: t('stock.available.label'),
    labelStockCheck: t('stock.trigger.collect'),
    labelStockCollect: t('stock.available.label'),
  } : {
    text: t('stock.trigger.incentive-text'),
    label: t('stock.trigger.label'),
    labelStockCheck: t('stock.trigger.check'),
    labelStockCollect: '',
  };

  const pdpView = () => {
    return (
      <div className="incentive">
        <Icon name="ico-location-circle" size={250} />
        <div className="incentive-text">
          <Typeset scale="1" bold tight tag="div">
            {incentive.text}
          </Typeset>
          <div className="buttons-wrap">
            {storeAssigned && (
              <a className="stock-collect" onClick={openStockCollect} data-test-id="component-stockCollect-trigger">
                {incentive.labelStockCollect}
              </a>
            )}
            <a className="stock-check" onClick={openStockCheck} data-test-id="component-stockCheck-trigger">
              {incentive.labelStockCheck}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const ankeilerView = () =>  {
    return (
      <div>
        <Typeset scale="0" bold tight tag="div">
          <Text>stock.trigger.incentive-short-text</Text>
        </Typeset>
        <a className="stock-check-trigger" data-test-id="component-stockCheck-trigger" onClick={openStockCheck}>
          <Typeset scale="0" tag="div">
            <Text>stock.trigger.check</Text>
          </Typeset>
        </a>
      </div>
    );
  };

  return (
    <div className="stockcheck" data-test-id="component-stockCheck">
      {loading ? (
        <Skeleton height="70px"/>
      ) : (
        <>
          {showIcon ? pdpView() : ankeilerView()}
        </>
      )}
    </div>
  );
};

StockCheck.propTypes = {
  showIcon: PropTypes.bool,
  product: PropTypes.object,
  store: PropTypes.object,
};

export default StockCheck;
